/**
 * The firm a request CLAIMS, from the `x-cova-org` header (spec multi-product
 * 005 §2 step 1, 002 §9.3, 001 §4; board unit SEAM-2).
 *
 * ═══ A CLAIM, NEVER AN AUTHORITY ═══
 *
 * The header is the client's statement of intent, not a fact about the
 * caller. Only a live, active membership in the named firm admits it, and that
 * check belongs to identity resolution (002 §9.3, step M-1): a claimed firm the caller holds an
 * active membership in is the firm the request resolves to; any other claim
 * changes nothing and is counted as the refusal enforcement will raise
 * (`requested-membership.ts`, WP0.32.2).
 *
 * ═══ A CLOSED PARSE ═══
 *
 * The only accepted shape is a UUID, normalised to lower case (organisation
 * ids are stored lower case). Anything else, `__proto__` and friends included
 * (the `admitScope` own-property lesson), is `Malformed` and claims nothing.
 * The raw value is never kept: it is attacker-controlled text and must not
 * reach a log, a metric label or an error message.
 *
 * Frontend-safe: `effect` and the framework-free contracts package only.
 */
import { ORG_HEADER } from "@cova/platform-contracts/access"
import { Data, FiberRef, GlobalValue, Option, Schema as S } from "effect"

/**
 * The header name, lower case as `Headers` stores keys. Declared in
 * `@cova/platform-contracts/access` (WP1.25.1) so apps send the same name.
 */
export { ORG_HEADER }

/**
 * An organisation id as CLAIMED by a request. Branded so a claimed id cannot
 * be passed where an admitted organisation is expected without a membership
 * check in between.
 */
export const RequestedOrgId = S.UUID.pipe(S.brand("RequestedOrgId"))
export type RequestedOrgId = typeof RequestedOrgId.Type

const decodeRequestedOrgId = S.decodeUnknownOption(RequestedOrgId)

/** The three shapes a raw `x-cova-org` value can take. */
export type OrgHeaderClaim = Data.TaggedEnum<{
  /** No header, or only whitespace: the client named no firm. */
  Missing: {}
  /** Present but not a UUID. Claims nothing; the value is discarded. */
  Malformed: {}
  /** A well-formed id. Still only a claim until membership admits it. */
  Claimed: { readonly orgId: RequestedOrgId }
}>

export const OrgHeaderClaim = Data.taggedEnum<OrgHeaderClaim>()

/** Classify a raw header value. Total: every input maps to one shape. */
export const classifyOrgHeader = (raw: string | null | undefined): OrgHeaderClaim => {
  const trimmed = raw?.trim() ?? ""
  if (trimmed === "") return OrgHeaderClaim.Missing()
  return Option.match(decodeRequestedOrgId(trimmed.toLowerCase()), {
    onNone: () => OrgHeaderClaim.Malformed(),
    onSome: (orgId) => OrgHeaderClaim.Claimed({ orgId }),
  })
}

/** The claimed firm, if the header names one well-formed id (005 Interfaces). */
export const parseOrgHeader = (raw: string | null | undefined): Option.Option<RequestedOrgId> => {
  const claim = classifyOrgHeader(raw)
  return claim._tag === "Claimed" ? Option.some(claim.orgId) : Option.none()
}

/**
 * The outcome of a claim against the caller's active memberships, the four
 * rows of the parse table (005 §2, 002 §9.3).
 */
export type OrgClaimOutcome = "valid" | "missing" | "malformed" | "not-a-member"

/**
 * Whether a membership's organisation is the claimed firm. The one comparison
 * every claim check uses: the claim is normalised lower case, a stored id is
 * compared case-insensitively, and a membership with no organisation matches
 * nothing.
 */
export const isClaimedOrg = (memberOrgId: string | null, claimed: RequestedOrgId): boolean =>
  memberOrgId !== null && memberOrgId.toLowerCase() === claimed

export const orgClaimOutcome = (
  claim: OrgHeaderClaim,
  memberOrgIds: ReadonlyArray<string>,
): OrgClaimOutcome =>
  OrgHeaderClaim.$match(claim, {
    Missing: () => "missing" as const,
    Malformed: () => "malformed" as const,
    Claimed: ({ orgId }) =>
      memberOrgIds.some((id) => isClaimedOrg(id, orgId)) ? ("valid" as const) : ("not-a-member" as const),
  })

/**
 * The firm the current request claims; `None` when it names none.
 *
 * `GlobalValue` for the reason `RequestScope` uses it: FiberRef
 * identity is per creation, and Next's HMR and per-route bundles evaluate a
 * module more than once, so a bare `unsafeMake` would hand the publisher one
 * ref and the reader another. The symbol string is a stable key, not a name.
 */
export const RequestedOrg: FiberRef.FiberRef<Option.Option<RequestedOrgId>> = GlobalValue.globalValue(
  Symbol.for("@cova/domain/organisation/RequestedOrg"),
  () => FiberRef.unsafeMake<Option.Option<RequestedOrgId>>(Option.none()),
)
